import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockIcon, FireIcon, ArchiveBoxIcon, UsersIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { ClockIcon as ClockIconSolid, FireIcon as FireIconSolid, ArchiveBoxIcon as ArchiveBoxIconSolid, UsersIcon as UsersIconSolid, ChartBarIcon as ChartBarIconSolid } from '@heroicons/react/24/solid';
import { PostFilter } from '../../hooks/useFilteredPosts';

interface PostFilterSelectorProps {
  currentFilter: PostFilter;
  onFilterChange: (filter: PostFilter) => void;
}

const PostFilterSelector: React.FC<PostFilterSelectorProps> = ({ currentFilter, onFilterChange }) => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<PostFilter>(currentFilter);
  
  // Actualizar el estado local cuando cambia la prop
  useEffect(() => {
    setActiveFilter(currentFilter);
  }, [currentFilter]);
  
  const filters: { id: PostFilter; name: string; icon: React.ReactElement; activeIcon: React.ReactElement; ariaLabel: string }[] = [
    { 
      id: 'following', 
      name: t('filters.following'), 
      icon: <UsersIcon className="h-5 w-5" />,
      activeIcon: <UsersIconSolid className="h-5 w-5" />,
      ariaLabel: t('aria.followingFilter')
    },
    { 
      id: 'recent', 
      name: t('filters.mostRecent'), 
      icon: <ClockIcon className="h-5 w-5" />,
      activeIcon: <ClockIconSolid className="h-5 w-5" />,
      ariaLabel: t('aria.recentFilter')
    },
    { 
      id: 'popular', 
      name: t('filters.mostPopular'), 
      icon: <FireIcon className="h-5 w-5" />,
      activeIcon: <FireIconSolid className="h-5 w-5" />,
      ariaLabel: t('aria.popularFilter')
    },
    { 
      id: 'trending', 
      name: t('filters.trending'), 
      icon: <ChartBarIcon className="h-5 w-5" />,
      activeIcon: <ChartBarIconSolid className="h-5 w-5" />,
      ariaLabel: t('aria.trendingFilter')
    },
    { 
      id: 'oldest', 
      name: t('filters.oldest'), 
      icon: <ArchiveBoxIcon className="h-5 w-5" />,
      activeIcon: <ArchiveBoxIconSolid className="h-5 w-5" />,
      ariaLabel: t('aria.oldestFilter')
    }
  ];

  const handleFilterChange = (filter: PostFilter) => {
    setActiveFilter(filter);
    onFilterChange(filter);
  };

  return (
    <div className="w-full mb-4 relative" aria-label={t('aria.postFilters')}>
      {/* Indicador de scroll horizontal en dispositivos móviles */}
      <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent opacity-50 md:hidden"></div>
      
      {/* Contenedor con scroll horizontal */}
      <div className="overflow-x-auto pb-3 hide-scrollbar" role="tablist">
        <div className="flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1 min-w-max shadow-sm">
          {filters.map((filter) => {
            const isActive = activeFilter === filter.id;
            return (
              <button
                key={filter.id}
                onClick={() => handleFilterChange(filter.id)}
                className={`
                  flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all
                  ${isActive 
                    ? 'bg-white dark:bg-gray-700 text-domired-600 dark:text-domired-400 shadow-sm ring-1 ring-domired-200 dark:ring-domired-800' 
                    : 'text-gray-600 dark:text-gray-400 hover:bg-white/[0.12] hover:text-gray-700 dark:hover:text-gray-300'}
                `}
                role="tab"
                aria-selected={isActive}
                aria-label={filter.ariaLabel}
                aria-controls={`filter-panel-${filter.id}`}
                data-filter={filter.id}
              >
                <span className="mr-2">
                  {isActive ? filter.activeIcon : filter.icon}
                </span>
                <span className="whitespace-nowrap">{filter.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Los estilos para ocultar la barra de desplazamiento se definen en CSS global */}
    </div>
  );
};

export default PostFilterSelector;
